/**
 * ProdutoBusca — acha o item no Cadastro de Suprimentos pelo nome ou código
 * (busca no servidor: o cadastro é grande demais para vir inteiro no
 * celular). Opcional: quem não acha segue descrevendo o item à mão.
 *
 * Com o produto vinculado, a autorização reserva o estoque e a separação dá
 * baixa; sem ele o item é só texto (e o Painel mostra quantos ficaram assim).
 *
 * Props: `valor` ({id, codigo, nome, unidade} | null), `onSelecionar(p|null)`.
 */
import React, { useEffect, useRef, useState } from "react";
import { FiX } from "react-icons/fi";

import { useAuth } from "../../../context/AuthContext";
import { buscarProdutosSolicitacao } from "../../../services/solicitacoes";
import {
  ComboDropdown, ComboItem, ComboLimparBtn, ComboVazio, ComboWrap, Input,
} from "./styles";

const ESPERA_MS = 300;
const MINIMO = 2;

export default function ProdutoBusca({ valor, onSelecionar, autoFocus }) {
  const { request } = useAuth();
  const [texto, setTexto] = useState(valor ? valor.nome : "");
  const [resultados, setResultados] = useState([]);
  const [aberto, setAberto] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const pedido = useRef(0);
  const blurTimer = useRef(null);

  useEffect(() => { setTexto(valor ? valor.nome : ""); }, [valor]);

  useEffect(() => {
    const busca = texto.trim();
    if (valor || busca.length < MINIMO) {
      setResultados([]);
      return undefined;
    }
    pedido.current += 1;
    const meu = pedido.current;
    const t = setTimeout(async () => {
      setCarregando(true);
      try {
        const r = await buscarProdutosSolicitacao(request, busca);
        if (pedido.current === meu) { setResultados(r || []); setErro(""); }
      } catch (e) {
        if (pedido.current === meu) setErro(e?.message || "Não foi possível buscar no cadastro.");
      } finally {
        if (pedido.current === meu) setCarregando(false);
      }
    }, ESPERA_MS);
    return () => clearTimeout(t);
  }, [texto, valor, request]);

  const escolher = (p) => {
    onSelecionar(p);
    setAberto(false);
  };

  const limpar = () => {
    onSelecionar(null);
    setTexto("");
    setResultados([]);
  };

  const buscaCurta = texto.trim().length < MINIMO;

  return (
    <ComboWrap>
      <Input
        autoFocus={autoFocus}
        value={texto}
        placeholder="Buscar por nome ou código"
        aria-label="Buscar item no Cadastro de Suprimentos"
        onChange={(e) => {
          setTexto(e.target.value);
          if (valor) onSelecionar(null);
          setAberto(true);
        }}
        onFocus={() => setAberto(true)}
        onBlur={() => { blurTimer.current = setTimeout(() => setAberto(false), 150); }}
        style={valor ? { paddingRight: 48 } : undefined}
      />
      {valor && (
        <ComboLimparBtn type="button" aria-label="Tirar o item do cadastro" onClick={limpar}>
          <FiX size={18} />
        </ComboLimparBtn>
      )}
      {aberto && !valor && !buscaCurta && (
        <ComboDropdown onMouseDown={() => clearTimeout(blurTimer.current)}>
          {carregando && <ComboVazio>Buscando…</ComboVazio>}
          {!carregando && erro && <ComboVazio>{erro}</ComboVazio>}
          {!carregando && !erro && resultados.length === 0 && (
            <ComboVazio>Nada no cadastro com esse nome. Siga descrevendo o item à mão.</ComboVazio>
          )}
          {!carregando && resultados.map((p) => (
            <ComboItem key={p.id} type="button" onClick={() => escolher(p)}>
              {p.nome}
              <small>{[p.codigo, p.unidade].filter(Boolean).join(" · ")}</small>
            </ComboItem>
          ))}
        </ComboDropdown>
      )}
    </ComboWrap>
  );
}
